import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FiSmartphone, FiLock, FiCode, 
  FiTerminal
} from 'react-icons/fi';

const SkillsSection = styled.section`
  padding: 100px 150px;
  background-color: var(--bg-secondary);
  
  @media (max-width: 768px) {
    padding: 80px 25px;
  }
`;

const SkillsContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const SkillsTitle = styled(motion.h2)`
  display: flex;
  align-items: center;
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 40px;
  color: var(--text-primary);
  
  &::after {
    content: "";
    display: block;
    width: 300px;
    height: 1px;
    margin-left: 20px;
    background-color: var(--bg-highlight);
  }
  
  @media (max-width: 768px) {
    font-size: 24px;
    
    &::after {
      width: 100px;
    }
  }
`;

const SkillsDescription = styled(motion.p)`
  color: var(--text-secondary);
  font-size: 18px;
  margin-bottom: 40px;
  line-height: 1.6;
  max-width: 650px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin-top: 40px;
`;

const CardHeading = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  
  svg {
    color: var(--text-secondary);
    font-size: 32px;
    margin-right: 15px;
    transition: all 0.3s ease;
  }
  
  h3 {
    color: var(--text-primary);
    font-family: var(--font-mono);
    font-size: 18px;
    flex: 1;
  }
`;

const SkillCard = styled(motion.div)`
  background: var(--bg-primary);
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
  position: relative;
  overflow: hidden;
  height: 100%;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  border: 1px solid var(--bg-highlight);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px -20px rgba(2, 12, 27, 0.7);
    border-color: var(--accent-primary);
    
    ${CardHeading} svg {
      color: var(--accent-primary);
      transform: translateY(-3px);
    }
  }
`;

const SkillList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const SkillItem = styled(motion.li)`
  margin-bottom: 15px;
  padding-left: 20px;
  position: relative;
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.5;
  
  &::before {
    content: '▹';
    position: absolute;
    left: 0;
    color: var(--accent-primary);
  }
`;

const ToolsTitle = styled(SkillsTitle)`
  display: flex;
  align-items: center;
  font-size: 24px;
  margin-top: 60px;
  
  @media (max-width: 768px) {
    font-size: 20px;
    flex-direction: row;
    align-items: center;
    flex-wrap: nowrap;
    white-space: nowrap;
    
    span {
      display: inline-block;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    &::after {
      width: 60px;
      min-width: 60px;
      margin-left: 10px;
    }
  }
`;

const ToolGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
  margin-top: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-top: 20px;
  }
`;

const ToolItem = styled(motion.div)`
  background-color: var(--bg-primary);
  border-radius: 6px;
  padding: 12px;
  font-family: var(--font-mono);
  font-size: 14px;
  color: var(--text-secondary);
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.2s ease-in-out;
  text-align: center;
  border: 1px solid var(--bg-highlight);
  
  &:hover {
    background-color: var(--bg-primary);
    color: var(--accent-primary);
    transform: translateY(-5px);
    border-color: var(--accent-primary);
    box-shadow: 0 10px 20px -15px rgba(2, 12, 27, 0.7);
  }
  
  @media (max-width: 768px) {
    padding: 10px 8px;
    font-size: 12px;
    min-height: 40px;
  }
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.08,
      duration: 0.3
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.3
    }
  }
};

const toolVariants = {
  hidden: { scale: 0.9, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: 0.15
    }
  }
};

const CustomShieldIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="32" 
    height="32" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="1.5" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    style={{ marginRight: '15px', position: 'relative', top: '2px' }}
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
  </svg>
);

// Custom Brain Icon
const CustomBrainIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="32" 
    height="32" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="1.5" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5V5a3 3 0 0 0 3 3h.5A2.5 2.5 0 0 1 18 10.5v1a2 2 0 0 0 2 2v-1a2 2 0 1 1 2 2h-1a2 2 0 0 0-2 2v.5a2.5 2.5 0 0 1-2.5 2.5h-.5a3 3 0 0 0-3 3 1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1 3 3 0 0 0-3-3h-.5A2.5 2.5 0 0 1 2 17.5v-.5a2 2 0 0 0-2-2h1a2 2 0 1 1-2-2h1a2 2 0 0 0 2-2v-.5A2.5 2.5 0 0 1 4.5 8H5a3 3 0 0 0 3-3V4.5A2.5 2.5 0 0 1 9.5 2z"></path>
  </svg>
);

const Skills = () => {
  const [titleRef, titleInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [descRef, descInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [gridRef, gridInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [toolsRef, toolsInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const skillCategories = [
    {
      title: "VAPT",
      icon: <CustomShieldIcon />,
      skills: [
        "Mobile (Android & iOS)",
        "Web Applications",
        "APIs",
        "Cloud",
        "Thick Client",
        "Network & Infrastructure"
      ]
    },
    {
      title: "AI Security",
      icon: <CustomBrainIcon />,
      skills: [
        "LLM Vulnerability Assessment",
        "Prompt Injection",
        "AI Model Hardening"
      ]
    },
    {
      title: "Android Security",
      icon: <FiSmartphone />,
      skills: [
        "Advanced Reverse Engineering",
        "Dynamic Analysis",
        "Malware Analysis",
        "Application Hardening",
      ]
    },
    {
      title: "Information Security",
      icon: <FiLock />,
      skills: [
        "Secure Code Review",
        "Threat Modeling",
        "Cloud Configuration Review",
        "OSINT",
        "Bug Bounty"
      ]
    },
    {
      title: "Programming",
      icon: <FiCode />,
      skills: [
        "Python (automation, scripting)",
        "Node.js (web development)",
        "PHP (web development)",
        "Java (code review)"
      ]
    }
  ];
  
  const tools = [
    "Frida", "MobSF", "RMS", "Jadx", "dnSpy", "Apktool",
    "Fiddler", "Binwalk", "Magisk", "Xposed", "Il2CppDumper",
    "CheckMarx", "ScoutSuite", "Burp Suite", "OWASP ZAP",
    "IDA", "Censys", "Shodan", "Nmap", "Metasploit",
    "Wireshark", "SSLyze", "SQLMap", "Postman", "Dirsearch", 
    "TFSec", "Semgrep", "CatchIT", "Prisma CWPP"
  ];
  
  return (
    <SkillsSection id="skills">
      <SkillsContainer>
        <SkillsTitle
          ref={titleRef}
          initial={{ y: 20, opacity: 0 }}
          animate={titleInView ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          Skills
        </SkillsTitle>
        
        <SkillsDescription
          ref={descRef}
          initial={{ y: 20, opacity: 0 }}
          animate={descInView ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          I've developed expertise in various cybersecurity domains through professional work, 
          continuous learning, and hands-on experience. Here's an overview of my technical skills:
        </SkillsDescription>
        
        <SkillsGrid
          ref={gridRef}
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate={gridInView ? "visible" : "hidden"}
        >
          {skillCategories.map((category, index) => (
            <SkillCard 
              key={index}
              variants={itemVariants}
              whileHover={{ y: -5 }}
            >
              <CardHeading>
                {category.icon}
                <h3>{category.title}</h3>
              </CardHeading>
              <SkillList>
                {category.skills.map((skill, i) => (
                  <SkillItem 
                    key={i}
                    variants={itemVariants}
                  >
                    {skill}
                  </SkillItem>
                ))}
              </SkillList>
            </SkillCard>
          ))}
        </SkillsGrid>
        
        <motion.div
          ref={toolsRef}
          initial={{ opacity: 0 }}
          animate={toolsInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <ToolsTitle
            animate={toolsInView ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
          >
            <FiTerminal style={{ marginRight: '10px', flexShrink: 0 }} /> 
            <span>Tools & Technologies</span>
          </ToolsTitle>
          
          <ToolGrid
            as={motion.div}
            variants={containerVariants}
            initial="hidden"
            animate={toolsInView ? "visible" : "hidden"}
          >
            {tools.map((tool, index) => (
              <ToolItem 
                key={index}
                variants={toolVariants}
                whileHover={{ scale: 1.05 }}
              >
                {tool}
              </ToolItem>
            ))}
          </ToolGrid>
        </motion.div>
      </SkillsContainer>
    </SkillsSection>
  );
};

export default Skills; 